import React, { useRef, useState, useCallback, DragEvent, ChangeEvent } from 'react';
import { acceptAttrForKey } from '../../utils/assetValidation';
import type { AssetKey } from '../../types';

interface FileDropZoneProps {
  assetKey: AssetKey;
  onFile: (file: File) => void;
  disabled?: boolean;
  compact?: boolean;
  children?: React.ReactNode;
}

export function FileDropZone({
  assetKey,
  onFile,
  disabled = false,
  compact = false,
  children,
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (disabled) return;
      const file = e.dataTransfer.files[0];
      if (file) onFile(file);
    },
    [disabled, onFile]
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback(() => setIsDragging(false), []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) onFile(file);
      // Reset so the same file can be re-selected
      e.target.value = '';
    },
    [onFile]
  );

  const handleClick = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick]
  );

  const accept = acceptAttrForKey(assetKey);

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="Upload file — click or drag and drop"
      aria-disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={[
        'relative rounded-lg border-2 border-dashed transition-colors duration-150 cursor-pointer select-none',
        compact ? 'p-2' : 'p-4',
        disabled
          ? 'border-zinc-700 bg-zinc-800/30 cursor-not-allowed opacity-50'
          : isDragging
          ? 'border-blue-400 bg-blue-500/10'
          : 'border-zinc-600 bg-zinc-800/50 hover:border-zinc-400 hover:bg-zinc-700/40',
      ].join(' ')}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        disabled={disabled}
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
      />
      {children ?? (
        <div className="flex flex-col items-center justify-center gap-1 text-center pointer-events-none">
          <UploadIcon className="w-5 h-5 text-zinc-400" />
          <p className="text-xs text-zinc-400">
            <span className="text-blue-400">Click</span> or drag file
          </p>
        </div>
      )}
    </div>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
      />
    </svg>
  );
}
